package com.evoapps.evoshift.domain.usecase.todo

import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.StaffCheck
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/*
 * SH-170: "Do zrobienia", one prioritised list on the home screen of every role. Each item is a
 * fact (what, how many, by when, where to act); the app words it in the reader's language and
 * gives it its one action (done in place, or the screen where it is done).
 */

/** One thing waiting for the signed-in person. [due]: the day it matters by (null: no day). */
sealed interface TodoItem {
    val kind: TodoKind
    val due: LocalDate?

    // --- Owners and managers -------------------------------------------------------------

    /** SH-164: requests waiting for a decision ([firstName]: whose is the earliest). */
    data class PendingRequests(
        val absences: Int,
        val swaps: Int,
        val firstName: String?,
        override val due: LocalDate?
    ) : TodoItem {
        override val kind get() = TodoKind.REQUESTS
        val count: Int get() = absences + swaps
    }

    /** SH-134: today's flagged or unplanned clock-ins to review. */
    data class ClockReviews(val count: Int, val date: LocalDate) : TodoItem {
        override val kind get() = TodoKind.CLOCK_REVIEWS
        override val due: LocalDate get() = date
    }

    /** SH-257: yesterday's shifts whose working hours no manager confirmed yet ("Potwierdź godziny z wczoraj"). */
    data class HoursToConfirm(val workplaceId: String, val date: LocalDate, val count: Int) : TodoItem {
        override val kind get() = TodoKind.HOURS_TO_CONFIRM
        override val due: LocalDate get() = date
    }

    /** SH-68: days of the last week whose takings are still missing, oldest first. */
    data class MissingTakings(val workplaceId: String, val days: List<LocalDate>) : TodoItem {
        override val kind get() = TodoKind.MISSING_TAKINGS
        override val due: LocalDate get() = days.first()
        val first: LocalDate get() = days.first()
    }

    /** SH-147: people working this month without pay terms ([firstUserId]: whose pay opens). */
    data class MissingPay(
        val workplaceId: String,
        val count: Int,
        val firstUserId: String,
        val firstName: String
    ) : TodoItem {
        override val kind get() = TodoKind.MISSING_PAY
        override val due: LocalDate? get() = null
    }

    /** Shifts nobody works yet in the next [TodoRules.UNFILLED_DAYS] days ([rosterId]: of the first one). */
    data class UnfilledShifts(val rosterId: String, val count: Int, val first: LocalDate) : TodoItem {
        override val kind get() = TodoKind.UNFILLED_SHIFTS
        override val due: LocalDate get() = first
    }

    /** An availability request whose answers are still missing before its deadline. */
    data class AvailabilityAnswers(
        val rosterId: String,
        val year: Int,
        val month: Int,
        val missing: Int,
        val team: Int,
        val deadline: LocalDate?,
        /** Those a reminder reaches (missing, with the app). */
        val remindable: List<String>
    ) : TodoItem {
        override val kind get() = TodoKind.AVAILABILITY_ANSWERS
        override val due: LocalDate? get() = deadline
    }

    /**
     * A month to publish, one item per month: a draft whose month starts within
     * [TodoRules.PUBLISH_DAYS] days (or already started), or (SH-263) a published month that has not
     * ended with [changes] shifts waiting for the next publish, the earliest on [firstChange].
     */
    data class RosterToPublish(
        val rosterId: String,
        val year: Int,
        val month: Int,
        val changes: Int = 0,
        val firstChange: LocalDate? = null
    ) : TodoItem {
        override val kind get() = TodoKind.ROSTER_TO_PUBLISH
        override val due: LocalDate get() = firstChange ?: LocalDate(year, month, 1)
        /** SH-263: published before, so these are changes, not the first publish. */
        val hasChanges: Boolean get() = changes > 0
    }

    // --- Employees -----------------------------------------------------------------------

    /** The person's next shift, starting within [TodoRules.NEXT_SHIFT_HOURS] hours. */
    data class NextShift(val shift: Shift, val startsInMinutes: Long) : TodoItem {
        override val kind get() = TodoKind.NEXT_SHIFT
        override val due: LocalDate get() = LocalDate.parse(shift.date)
    }

    /** The manager asked for availability and the person has not sent it yet. */
    data class AvailabilityDue(val rosterId: String, val year: Int, val month: Int, val deadline: LocalDate?) : TodoItem {
        override val kind get() = TodoKind.AVAILABILITY_DUE
        override val due: LocalDate? get() = deadline
    }

    /** Colleagues asked the person to take their shift ([firstName] on [first], the earliest). */
    data class SwapsAsked(val count: Int, val firstName: String, val first: LocalDate) : TodoItem {
        override val kind get() = TodoKind.SWAPS_ASKED
        override val due: LocalDate get() = first
    }

    /** The person's own swap request was rejected: they read it and dismiss it. */
    data class SwapRejected(val shiftId: String, val date: LocalDate) : TodoItem {
        override val kind get() = TodoKind.SWAP_REJECTED
        override val due: LocalDate get() = date
    }

    /** Workplace policies (regulaminy) the person has not confirmed. */
    data class PoliciesToConfirm(val workplaceId: String, val count: Int, val firstTitle: String) : TodoItem {
        override val kind get() = TodoKind.POLICIES
        override val due: LocalDate? get() = null
    }

    /** The person's own medical exam or OHS training ended or ends within 30 days. */
    data class OwnCheck(val check: StaffCheck, val validUntil: LocalDate, val expired: Boolean) : TodoItem {
        override val kind get() = TodoKind.OWN_CHECK
        override val due: LocalDate get() = validUntil
    }

    /** Shifts on the marketplace the person could take (the earliest on [first]). */
    data class Marketplace(val count: Int, val first: LocalDate) : TodoItem {
        override val kind get() = TodoKind.MARKETPLACE
        override val due: LocalDate get() = first
    }
}

/** The kinds, in their order of importance when none is urgent. */
enum class TodoKind {
    // Team (owners and managers)
    CLOCK_REVIEWS,
    REQUESTS,
    HOURS_TO_CONFIRM,
    MISSING_TAKINGS,
    AVAILABILITY_ANSWERS,
    ROSTER_TO_PUBLISH,
    UNFILLED_SHIFTS,
    MISSING_PAY,

    // Own (employees)
    NEXT_SHIFT,
    SWAPS_ASKED,
    AVAILABILITY_DUE,
    SWAP_REJECTED,
    OWN_CHECK,
    POLICIES,
    MARKETPLACE
}

object TodoRules {
    /** Unfilled shifts count from today over this many days. */
    const val UNFILLED_DAYS = 14

    /** Missing takings are looked for over this many days before today. */
    const val TAKINGS_DAYS = 7

    /** A draft is to be published once its month starts within this many days. */
    const val PUBLISH_DAYS = 10

    /** The next shift is on the list once it starts within this many hours. */
    const val NEXT_SHIFT_HOURS = 24

    /** An item whose day is at most this many days away is urgent: it comes first. */
    const val URGENT_DAYS = 1

    /**
     * Most urgent first: items due by tomorrow (or overdue), then by importance ([TodoKind]'s
     * order), then the earlier day.
     */
    fun sorted(items: List<TodoItem>, today: LocalDate): List<TodoItem> {
        val urgentBy = today.plus(DatePeriod(days = URGENT_DAYS))
        return items.sortedWith(
            compareBy<TodoItem> { item -> if (item.due?.let { it <= urgentBy } == true) 0 else 1 }
                .thenBy { it.kind.ordinal }
                .thenBy { it.due }
        )
    }
}
